import {
  IFormDefinition,
  IFormValues,
  PendingRemoveAttachmentOperations,
} from "@entaconsulting/form";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import isUnauthenticatedErrorAction from "common/api/utils/isUnauthenticatedErrorAction";
import { sleep } from "common/utils/sleep";
import { AppThunk, RootState } from "store";
import {
  getFormData,
  getFormDefinition,
  postForm,
  putFormDefinitionMetadata,
  putSampleData,
} from "store/form/api/formApi";
import {
  formDefinition as testFormDefinition,
  initialData as testInitialData,
} from "test-data/SampleFormDefinition";
import { ISharedFormInstance } from "./api/types";

export type FormMode = "test" | "fill";
export interface DynamicFormState {
  formMode: FormMode;
  formDefinitionId: string;
  formDefinition?: IFormDefinition;
  initialValues: IFormValues;
  eTag: string;
  isLoading: boolean;
  error?: string;
  isSubmitting: boolean;
  attachmentsWorkspaceId: string;
  formState: FormState;
  allowEdit: boolean;
  lastSaved?: string;
}

export type FormState = "Draft" | "Sent";

export const fetchDeveloperForm = createAsyncThunk(
  "form/fetchDeveloperForm",
  async (formDefinitionId: string) => {
    const dynamicFormDefinition = await getFormDefinition(formDefinitionId);
    return {
      formDefinitionId: formDefinitionId,
      dynamicFormDefinition,
    };
  }
);

export const saveSampleData = createAsyncThunk<
  IFormValues,
  IFormValues,
  {
    state: RootState;
  }
>("form/saveSampleData", async (values: IFormValues, { getState }) => {
  const {
    formData: { formDefinitionId },
  } = getState();

  await putSampleData(formDefinitionId, values);
  return values;
});

export const saveFormDefinition = createAsyncThunk<
  { formDefinition: IFormDefinition; eTag: string },
  { formDefinition: IFormDefinition; formDefinitionId: string }
>("form/saveFormDefinition", async (arg) => {
  const { formDefinition, formDefinitionId } = arg;

  if (!formDefinition) {
    throw new Error("Cannot save formDefinition as it's undefined");
  }

  const response = await putFormDefinitionMetadata(
    formDefinitionId,
    formDefinition
  );
  return { formDefinition, eTag: response.eTag };
});

const initialState: DynamicFormState = {
  formMode: "fill",
  formDefinitionId: "",
  initialValues: {},
  eTag: "",
  isLoading: false,
  isSubmitting: false,
  error: undefined,
  attachmentsWorkspaceId: "",
  formState: "Draft",
  allowEdit: false,
};
const formDataSlice = createSlice({
  name: "formData",
  initialState: initialState,
  reducers: {
    loadFormDataStart(
      state,
      action: PayloadAction<{ formDefinitionId: string }>
    ) {
      return {
        ...initialState,
        formDefinitionId: action.payload.formDefinitionId,
        isLoading: true,
      };
    },
    loadFormDataSuccess(
      state,
      action: PayloadAction<{
        formMode: FormMode;
        formDefinitionId: string;
        formData: ISharedFormInstance;
      }>
    ) {
      const formData = action.payload.formData;
      state.formMode = action.payload.formMode;
      state.formDefinitionId = action.payload.formDefinitionId;
      state.eTag = formData.eTag;
      state.formDefinition = formData.formDefinition;
      state.initialValues = formData.initialValues;
      state.lastSaved = formData.lastSaved;
      state.formState = formData.state;
      state.allowEdit = formData.state === "Draft";
      state.attachmentsWorkspaceId = formData.attachmentsWorkspaceId;
      state.isLoading = false;
      state.error = undefined;
    },
    loadFormDataFailed(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    submitFormStart(state) {
      state.isSubmitting = true;
    },
    submitFormSuccess(
      state,
      action: PayloadAction<{
        eTag: string;
        attachmentsWorkspaceId: string;
        lastSaved: string;
        state: FormState;
      }>
    ) {
      state.eTag = action.payload.eTag;
      state.attachmentsWorkspaceId = action.payload.attachmentsWorkspaceId;
      state.lastSaved = action.payload.lastSaved;
      state.formState = action.payload.state;
      state.allowEdit = action.payload.state === "Draft";
      state.isSubmitting = false;
      state.error = "";
    },
    submitFormFailed(state, action: PayloadAction<string>) {
      state.isSubmitting = false;
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeveloperForm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDeveloperForm.fulfilled, (state, action) => {
        const { dynamicFormDefinition: formData, formDefinitionId } =
          action.payload;
        state.formMode = "test";
        state.formDefinitionId = formDefinitionId;
        state.eTag = "";
        state.formDefinition = formData.formDefinition;
        state.initialValues = formData.initialValues;
        state.lastSaved = undefined;
        state.formState = "Draft";
        state.allowEdit = true;
        state.attachmentsWorkspaceId = "";
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(fetchDeveloperForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveSampleData.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(saveSampleData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = undefined;
        state.initialValues = action.payload;
      })
      .addCase(saveSampleData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveFormDefinition.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(saveFormDefinition.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.error = "";
        state.formDefinition = action.payload.formDefinition;
      })
      .addCase(saveFormDefinition.rejected, (state, action) => {
        state.isSubmitting = false;

        if (isUnauthenticatedErrorAction(action)) return;

        state.error = action.error.message;
      });
  },
});

export const {
  loadFormDataStart,
  loadFormDataSuccess,
  loadFormDataFailed,
  submitFormSuccess,
} = formDataSlice.actions;
const { submitFormStart, submitFormFailed } = formDataSlice.actions;

export default formDataSlice.reducer;

//PENDING: migrate to createAsyncThunk
export const fetchFormData =
  (formDefinitionId: string): AppThunk =>
  async (dispatch) => {
    dispatch(loadFormDataStart);
    if (formDefinitionId !== "test") {
      getFormData(formDefinitionId).then(
        (formData) =>
          dispatch(
            loadFormDataSuccess({
              formMode: "fill",
              formDefinitionId,
              formData,
            })
          ),
        (err) => dispatch(loadFormDataFailed(err.toString()))
      );
    } else {
      dispatch(
        loadFormDataSuccess({
          formMode: "test",
          formDefinitionId,
          formData: {
            formDefinition: testFormDefinition as IFormDefinition,
            initialValues: testInitialData as IFormValues,
            eTag: "",
            attachmentsWorkspaceId: "",
            state: "Draft",
          },
        })
      );
    }
  };

export const submitForm =
  (values: IFormValues, draft: boolean): AppThunk =>
  async (dispatch, getState) => {
    dispatch(submitFormStart());

    const {
      eTag,
      attachmentsWorkspaceId,
      formMode,
      formDefinitionId: sharedFormToken,
    } = getState().formData;

    const pendingAttachmentOperations = getState().attachments.items;
    const pendingRemoveAttachmentOperations: PendingRemoveAttachmentOperations[] =
      Object.keys(pendingAttachmentOperations).map((fieldName) => {
        return {
          fieldName,
          fileNames:
            pendingAttachmentOperations[fieldName]?.submitOperations
              .filter((so) => so.operation === "remove")
              .map((so) => so.fileName) || [],
        };
      });

    if (formMode === "fill") {
      postForm(
        sharedFormToken,
        values,
        pendingRemoveAttachmentOperations,
        eTag,
        attachmentsWorkspaceId,
        draft
      ).then(
        (response) =>
          dispatch(
            submitFormSuccess({
              eTag: response.eTag,
              attachmentsWorkspaceId: response.attachmentsWorkspaceId,
              lastSaved: response.lastSaved,
              state: response.state,
            })
          ),
        (err) => dispatch(submitFormFailed(err.toString()))
      );
      //setFormData({ ...formData, eTag: newETag });
    } else {
      await sleep(1000);
      dispatch(
        submitFormSuccess({
          eTag: "",
          attachmentsWorkspaceId: "",
          state: "Draft",
          lastSaved: new Date().toString(),
        })
      );
    }
  };
